import React, { Component } from 'react';
import { logger } from '../../../helpers';
import styles from './debug-panel.module.css';

class DebugPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            logs: [],
            filterLevel: 'INFO',
            searchTerm: '',
            showOnlyCurrentSession: true,
            refreshKey: 0
        };
    }

    componentDidMount() {
        this.refreshLogs();
        // Auto-refresh logs every 5 seconds when panel is open
        this.interval = setInterval(() => {
            if (this.state.isOpen) {
                this.refreshLogs();
            }
        }, 5000);
    }

    componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    refreshLogs = () => {
        const logs = logger.getLogs(this.state.filterLevel);
        this.setState({
            logs,
            refreshKey: this.state.refreshKey + 1
        });
    }

    togglePanel = () => {
        this.setState(
            { isOpen: !this.state.isOpen },
            () => {
                if (this.state.isOpen) {
                    this.refreshLogs();
                }
            }
        );
    }

    handleFilterChange = (event) => {
        const filterLevel = event.target.value;
        this.setState({ filterLevel }, () => {
            this.refreshLogs();
        });
    }

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    }

    exportLogs = () => {
        logger.exportLogs();
        logger.info('Debug panel: Logs exported by user');
    }

    clearLogs = () => {
        if (window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
            logger.clearLogs();
            this.refreshLogs();
        }
    }

    copyLogsToClipboard = async () => {
        try {
            const logsText = JSON.stringify(this.state.logs, null, 2);
            await navigator.clipboard.writeText(logsText);
            logger.info('Debug panel: Logs copied to clipboard');
            alert('Logs copied to clipboard!');
        } catch (error) {
            logger.error('Debug panel: Failed to copy logs to clipboard', { error });
            alert('Failed to copy logs to clipboard');
        }
    }

    getFilteredLogs = () => {
        let filtered = this.state.logs;

        if (this.state.showOnlyCurrentSession) {
            filtered = filtered.filter(log => log.sessionId === logger.sessionId);
        }

        if (this.state.searchTerm) {
            const searchLower = this.state.searchTerm.toLowerCase();
            filtered = filtered.filter(log =>
                log.message.toLowerCase().includes(searchLower) ||
                JSON.stringify(log.data).toLowerCase().includes(searchLower)
            );
        }

        return filtered;
    }

    formatLogData = (data) => {
        if (!data || Object.keys(data).length === 0) return null;
        return JSON.stringify(data, null, 2);
    }

    getLogLevelClass = (level) => {
        switch (level) {
            case 'ERROR': return styles.logError;
            case 'WARN': return styles.logWarn;
            case 'INFO': return styles.logInfo;
            case 'DEBUG': return styles.logDebug;
            default: return '';
        }
    }

    render() {
        if (!this.state.isOpen) {
            return (
                <div className={styles.debugToggle}>
                    <button
                        onClick={this.togglePanel}
                        className={styles.toggleButton}
                        title="Open Debug Panel"
                    ><span>Debug</span></button>
                </div>
            );
        }

        const filteredLogs = this.getFilteredLogs();
        const stats = logger.getLogStats();

        return (
            <div className={styles.debugPanel}>
                <div className={styles.header}>
                    <h3>Debug Panel</h3>
                    <button
                        onClick={this.togglePanel}
                        className={styles.closeButton}
                        title="Close Debug Panel"
                    ><span>✕</span></button>
                </div>

                <div className={styles.stats}>
                    <div className={styles.stat}>
                        <strong>Session:</strong> {logger.sessionId}
                    </div>
                    <div className={styles.stat}>
                        <strong>Total Logs:</strong> {stats.total}
                    </div>
                    <div className={styles.stat}>
                        <strong>Errors:</strong> {stats.byLevel.ERROR || 0}
                    </div>
                    <div className={styles.stat}>
                        <strong>Warnings:</strong> {stats.byLevel.WARN || 0}
                    </div>
                </div>

                <div className={styles.controls}>
                    <div className={styles.controlGroup}>
                        <label>
                            Filter Level:
                            <select
                                value={this.state.filterLevel}
                                onChange={this.handleFilterChange}
                            >
                                <option value="DEBUG">DEBUG</option>
                                <option value="INFO">INFO</option>
                                <option value="WARN">WARN</option>
                                <option value="ERROR">ERROR</option>
                            </select>
                        </label>
                    </div>

                    <div className={styles.controlGroup}>
                        <label>
                            Search:
                            <input
                                type="text"
                                value={this.state.searchTerm}
                                onChange={this.handleSearchChange}
                                placeholder="Search logs..."
                            />
                        </label>
                    </div>

                    <div className={styles.controlGroup}>
                        <label>
                            <input
                                type="checkbox"
                                checked={this.state.showOnlyCurrentSession}
                                onChange={(e) => this.setState({ showOnlyCurrentSession: e.target.checked })}
                            />
                            <span>Current Session Only</span>
                        </label>
                    </div>
                </div>

                <div className={styles.actions}>
                    <button onClick={this.refreshLogs} className={styles.actionButton}>
                        <span>Refresh</span>
                    </button>
                    <button onClick={this.exportLogs} className={styles.actionButton}>
                        <span>Export</span>
                    </button>
                    <button onClick={this.copyLogsToClipboard} className={styles.actionButton}>
                        <span>Copy</span>
                    </button>
                    <button onClick={this.clearLogs} className={styles.actionButton}>
                        <span>Clear</span>
                    </button>
                </div>

                <div className={styles.logContainer}>
                    {filteredLogs.length === 0 ? (
                        <div className={styles.noLogs}>No logs match the current filters</div>
                    ) : (
                        filteredLogs.slice(-50).reverse().map((log, index) => (
                            <div
                                key={`${log.timestamp}-${index}`}
                                className={`${styles.logEntry} ${this.getLogLevelClass(log.level)}`}
                            >
                                <div className={styles.logHeader}>
                                    <span className={styles.logTime}>
                                        {new Date(log.timestamp).toLocaleTimeString()}
                                    </span>
                                    <span className={styles.logLevel}>
                                        {log.level}
                                    </span>
                                </div>
                                <div className={styles.logMessage}>
                                    {log.message}
                                </div>
                                {this.formatLogData(log.data) && (
                                    <div className={styles.logData}>
                                        <pre>{this.formatLogData(log.data)}</pre>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    }
}

export default DebugPanel;